import { useTranslation } from "react-i18next";
import { IS_IOS_APP } from "../../config/appTarget.js";
import { ANDROID_STORE_URL, IOS_STORE_URL } from "../../lib/appStoreLinks.js";
import { useAppUpdate } from "./useAppUpdate.js";
import { UpdatePromptModal } from "./UpdatePromptModal.js";

/**
 * Gates the app on the native update state:
 *   - `forceUpdate` -> a full-screen blocking "please update" screen (the binary
 *     is below the backend's minimum supported build).
 *   - otherwise renders children, plus a dismissible soft "nova versao" prompt
 *     when a newer build is live in the store.
 * On web both are inert, so children render untouched.
 */
export function ForceUpdateGate({ children }: { children: React.ReactNode }) {
  const { forceUpdate, updateAvailable } = useAppUpdate();
  const { t } = useTranslation("features");

  if (!forceUpdate) {
    return (
      <>
        {children}
        <UpdatePromptModal updateAvailable={updateAvailable} />
      </>
    );
  }

  const storeUrl = IS_IOS_APP ? IOS_STORE_URL : ANDROID_STORE_URL;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-[#f9f7f4] px-8 text-center"
      style={IS_IOS_APP ? { paddingTop: "env(safe-area-inset-top)" } : undefined}
    >
      <h1 className="text-2xl font-bold text-my-black">
        {t("appUpdate.forceTitle")}
      </h1>
      <p className="max-w-sm text-my-black/80">
        {t("appUpdate.forceBody")}
      </p>
      <a
        href={storeUrl}
        target="_blank"
        rel="noreferrer"
        className="rounded-full bg-main-purple px-8 py-3 font-semibold text-white"
      >
        {t("appUpdate.updateNow")}
      </a>
    </div>
  );
}
